import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Snackbar
} from '@mui/material';
import { Home as HomeIcon, ReportProblem as ReportProblemIcon } from '@mui/icons-material';
import { resourceService } from '../services/resourceService';
import { lessonPlanService } from '../services/lessonPlanService';
import { LessonPlanFileViewer } from '../components/LessonPlanFileViewer';
import ResourceViewer from '../components/ResourceViewer';
import type { Resource, LessonPlan } from '../types';

const PublicResourcePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isLessonPlan = location.pathname.includes('/public/lesson-plan/');

  // Read token from URL search params (re-read on location change)
  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const token = searchParams.get('token');

  const [resource, setResource] = useState<Resource | null>(null);
  const [lessonPlan, setLessonPlan] = useState<LessonPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);

  useEffect(() => {
    if (id) {
      if (isLessonPlan) {
        loadLessonPlan();
      } else {
        loadResource();
      }
    }
  }, [id, isLessonPlan]);

  const loadResource = async () => {
    if (!id) return;

    if (!token) {
      setError('Link này yêu cầu token để truy cập. Vui lòng sử dụng link được tạo từ nút "Copy".');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get resource info using public endpoint with token
      const resourceData = await resourceService.getResourceByIdPublic(id, token);
      setResource(resourceData);
      setViewerOpen(true);

      // Increment view count when page loads (with token)
      await resourceService.viewResourcePublic(id, token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Có lỗi xảy ra');
    } finally {
      setLoading(false);
    }
  };

  const loadLessonPlan = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      // Get lesson plan info using public endpoint
      const lessonPlanData = await lessonPlanService.getLessonPlanByIdPublic(id);
      setLessonPlan(lessonPlanData);
      setViewerOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Có lỗi xảy ra');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!id) return;

    try {
      if (isLessonPlan && lessonPlan) {
        await lessonPlanService.downloadLessonPlanPublic(id, lessonPlan.fileName || 'lesson-plan');
      } else if (resource && token) {
        const blob = await resourceService.downloadResourcePublic(id, token);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = resource.fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      const errorMessage = err instanceof Error && err.message === 'FILE_NOT_FOUND'
        ? 'FILE_NOT_FOUND'
        : 'Không thể tải file. Vui lòng thử lại.';
      setError(errorMessage);
    }
  };

  const handleReportError = async () => {
    if (!id) return;

    try {
      setReporting(true);
      // For public page, we can't use authenticated endpoint, so we'll create a public report endpoint
      // For now, we'll use a workaround - try to call the endpoint without auth
      const response = await fetch(`/api/resources/public/${id}/report-error`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ reason: 'File không tồn tại trên server' })
      });

      if (response.ok) {
        setReportSuccess(true);
      }
    } catch (err) {
      // Silently fail
    } finally {
      setReporting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress />
        <Typography>{isLessonPlan ? 'Đang tải giáo án...' : 'Đang tải tài nguyên...'}</Typography>
      </Box>
    );
  }

  if (error && !resource && !lessonPlan) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
          <Button
            variant="contained"
            startIcon={<HomeIcon />}
            onClick={() => navigate('/')}
          >
            Về trang chủ
          </Button>
        </Paper>
      </Container>
    );
  }

  if (!resource && !lessonPlan) {
    return null;
  }

  const title = isLessonPlan ? (lessonPlan?.title || '') : (resource?.title || '');
  const description = isLessonPlan ? undefined : resource?.description;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="lg">
        <Paper sx={{ p: 3, mb: 2 }}>
          <Typography variant="h5" gutterBottom>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {description}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {isLessonPlan ? (
              <>
                {lessonPlan?.subject && (
                  <Typography variant="body2">
                    <strong>Môn học:</strong> {lessonPlan.subject}
                  </Typography>
                )}
                {lessonPlan?.week && (
                  <Typography variant="body2">
                    <strong>Tuần:</strong> {lessonPlan.week}
                  </Typography>
                )}
                {lessonPlan?.fileName && (
                  <Typography variant="body2">
                    <strong>File:</strong> {lessonPlan.fileName}
                  </Typography>
                )}
                {lessonPlan?.submissionDate && (
                  <Typography variant="body2">
                    <strong>Ngày nộp:</strong> {new Date(lessonPlan.submissionDate).toLocaleDateString('vi-VN')}
                  </Typography>
                )}
              </>
            ) : (
              <>
                <Typography variant="body2">
                  <strong>Loại file:</strong> {resource?.fileType.toUpperCase()}
                </Typography>
                {resource?.fileSize && (
                  <Typography variant="body2">
                    <strong>Kích thước:</strong> {resourceService.formatFileSize(resource.fileSize)}
                  </Typography>
                )}
                {resource?.viewCount !== undefined && (
                  <Typography variant="body2">
                    <strong>Lượt xem:</strong> {resource.viewCount}
                  </Typography>
                )}
                {resource?.downloadCount !== undefined && (
                  <Typography variant="body2">
                    <strong>Lượt tải:</strong> {resource.downloadCount}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Paper>

        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => setViewerOpen(true)}
            sx={{ mr: 2 }}
          >
            {isLessonPlan ? 'Xem giáo án' : 'Xem tài nguyên'}
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={handleDownload}
          >
            Tải về
          </Button>
        </Box>
      </Container>

      {isLessonPlan && lessonPlan && (
        <LessonPlanFileViewer
          lessonPlan={lessonPlan}
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          onDownload={handleDownload}
        />
      )}

      {!isLessonPlan && resource && (
        <PublicResourceViewer
          resource={resource}
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          onDownload={handleDownload}
          token={token || ''}
        />
      )}

      <Snackbar
        open={reportSuccess}
        autoHideDuration={3000}
        onClose={() => setReportSuccess(false)}
        message="Đã gửi báo cáo thành công. Cảm ơn bạn đã phản hồi!"
      />
    </Box>
  );
};

// Public version of ResourceViewer that uses public endpoints
interface PublicResourceViewerProps {
  resource: Resource;
  open: boolean;
  onClose: () => void;
  onDownload?: () => void;
  token: string;
}

const PublicResourceViewer: React.FC<PublicResourceViewerProps> = ({
  resource,
  open,
  onClose,
  onDownload,
  token
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [reporting, setReporting] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);

  useEffect(() => {
    if (open && resource) {
      loadResource();
    }
  }, [open, resource]);

  const loadResource = async () => {
    if (!resource) return;

    setLoading(true);
    setError(null);

    try {
      const fileTypeLower = resource.fileType.toLowerCase();
      const isImage = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'].includes(fileTypeLower);
      const isPDF = fileTypeLower === '.pdf';
      const isVideo = ['.mp4', '.avi', '.mov', '.wmv', '.webm'].includes(fileTypeLower);
      const isAudio = ['.mp3', '.wav', '.flac', '.aac', '.ogg'].includes(fileTypeLower);
      const isOffice = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'].includes(fileTypeLower);
      const isViewable = isPDF || isImage || isVideo || isAudio;
      const canView = isViewable || isOffice;

      if (canView) {
        // Use public endpoint to get file
        const blob = await resourceService.getResourceFilePublic(resource.id, token);
        const url = URL.createObjectURL(blob);
        setFileUrl(url);
      } else {
        setError('Loại file này không được hỗ trợ xem trực tiếp. Vui lòng tải về để xem.');
      }
    } catch (err) {
      const errorMessage = err instanceof Error && err.message === 'FILE_NOT_FOUND'
        ? 'FILE_NOT_FOUND'
        : err instanceof Error ? err.message : 'Không thể tải tài nguyên';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (fileUrl) {
      URL.revokeObjectURL(fileUrl);
      setFileUrl(null);
    }
    setError(null);
    onClose();
  };

  const handleReportError = async () => {
    if (!resource) return;

    try {
      setReporting(true);
      const response = await fetch(`/api/resources/public/${resource.id}/report-error`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ reason: 'File không tồn tại trên server' })
      });

      if (response.ok) {
        setReportSuccess(true);
      }
    } catch (err) {
      // Silently fail
    } finally {
      setReporting(false);
    }
  };

  const fileTypeLower = resource.fileType.toLowerCase();
  const isPDF = fileTypeLower === '.pdf';
  const isImage = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp'].includes(fileTypeLower);
  const isVideo = ['.mp4', '.avi', '.mov', '.wmv', '.webm'].includes(fileTypeLower);
  const isAudio = ['.mp3', '.wav', '.flac', '.aac', '.ogg'].includes(fileTypeLower);
  const isOffice = ['.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'].includes(fileTypeLower);
  const isViewable = isPDF || isImage || isVideo || isAudio;
  const canView = isViewable || isOffice;

  if (!open) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        bgcolor: 'background.default',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">{resource.title}</Typography>
        <Box>
          {onDownload && (
            <Button onClick={onDownload} sx={{ mr: 1 }}>
              Tải về
            </Button>
          )}
          <Button onClick={handleClose}>Đóng</Button>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        {loading && <CircularProgress />}
        {error && (
          <Box sx={{ textAlign: 'center', maxWidth: 600 }}>
            {error === 'FILE_NOT_FOUND' ? (
              <Alert
                severity="warning"
                icon={<ReportProblemIcon />}
                action={
                  <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column', mt: 1 }}>
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => loadResource()}
                      disabled={loading}
                    >
                      Thử lại
                    </Button>
                    <Button
                      color="inherit"
                      size="small"
                      startIcon={<ReportProblemIcon />}
                      onClick={handleReportError}
                      disabled={reporting || reportSuccess}
                    >
                      {reportSuccess ? 'Đã gửi' : 'Gửi báo cáo'}
                    </Button>
                  </Box>
                }
              >
                <Typography variant="body1" fontWeight="medium" gutterBottom>
                  Không tìm thấy file
                </Typography>
                <Typography variant="body2">
                  File này có thể đã bị xóa hoặc không tồn tại trên server. Vui lòng liên hệ quản trị viên nếu bạn cần hỗ trợ.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="error">{error}</Alert>
            )}
          </Box>
        )}
        {!loading && !error && canView && fileUrl && (
          <>
            {isPDF && (
              <iframe
                src={fileUrl}
                width="100%"
                height="100%"
                style={{ border: 'none' }}
                title={`PDF Viewer - ${resource.title}`}
              />
            )}
            {isImage && (
              <img
                src={fileUrl}
                alt={resource.title}
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            )}
            {isVideo && (
              <video src={fileUrl || undefined} controls style={{ width: '100%', maxHeight: '80vh' }} />
            )}
            {isAudio && (
              <audio src={fileUrl || undefined} controls style={{ width: '100%' }} />
            )}
            {isOffice && (
              <Box sx={{ textAlign: 'center', p: 4 }}>
                <Typography variant="h6" gutterBottom>
                  Tài liệu Office
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  File {resource.fileType.toUpperCase()} đã được tải thành công. Vui lòng tải về để xem chi tiết.
                </Typography>
                {onDownload && (
                  <Button variant="contained" onClick={onDownload}>
                    Tải File
                  </Button>
                )}
              </Box>
            )}
          </>
        )}
        {!loading && !error && !canView && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Loại File Không Được Hỗ Trợ Xem
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Loại file này ({resource.fileType}) không thể xem trực tiếp. Vui lòng tải về để xem.
            </Typography>
            {onDownload && (
              <Button variant="contained" onClick={onDownload}>
                Tải File
              </Button>
            )}
          </Box>
        )}
      </Box>

      <Snackbar
        open={reportSuccess}
        autoHideDuration={3000}
        onClose={() => setReportSuccess(false)}
        message="Đã gửi báo cáo thành công. Cảm ơn bạn đã phản hồi!"
      />
    </Box>
  );
};

export default PublicResourcePage;

